/**
 * Input-tag catalogue for the computed-tag editor.
 *
 * The editor picks its expression inputs from a `CatalogTag[]`. Stub mode uses
 * `FX_CATALOG_TAGS`; live mode maps the org's real tags (`GET /v1/iot/tags`) onto
 * the same shape, so the editor stays oblivious (#165).
 *
 * Also owns alias minting, which is NOT cosmetic: the alias is the math.js
 * variable name the server validates the expression against, so a bad alias is a
 * 400 on save.
 */

import { useMemo } from "react";
import { isLiveApi } from "@/lib/api";
import { useOrgTagCatalogQuery } from "@/lib/api/queries";
import type { TagOption } from "@/lib/api/twin-bindings";
import { FX_CATALOG_TAGS, type CatalogTag } from "./__fixtures";
import type { ComputedTagInput } from "./types";
import type { DataKind } from "@foundry/foundry-plant";

/** The server clamps `GET /v1/iot/tags` to this many rows regardless of `limit`. */
export const ORG_TAG_PAGE_CAP = 200;

/**
 * One org tag → the editor's catalogue shape.
 *
 * `source` narrows to the two values the editor renders; anything else
 * (`manual`, `ble`) is a hand-keyed datapoint, which is a perfectly good
 * computed-tag input, so it maps to "sensor" rather than being dropped.
 */
export function toCatalogTag(t: TagOption): CatalogTag {
  return {
    id: t.id,
    displayName: t.thing_name ? `${t.thing_name} · ${t.name}` : t.name,
    unit: t.unit || undefined,
    source: t.source === "computed" ? "computed" : "sensor",
    dataKind: (t.data_kind || undefined) as DataKind | undefined,
  };
}

/**
 * The catalogue the editor picks inputs from — fixtures in stub mode, the org's
 * real tags in live mode.
 *
 * `truncated` is true when the response hit the server's page cap, so the caller
 * can say "showing the first N" instead of silently presenting a short list as if
 * it were the whole catalogue.
 */
export function useTagCatalog(): { tags: CatalogTag[]; truncated: boolean; loading: boolean } {
  const query = useOrgTagCatalogQuery();
  return useMemo(() => {
    if (!isLiveApi) return { tags: FX_CATALOG_TAGS, truncated: false, loading: false };
    const items = query.data?.items ?? [];
    return {
      tags: items.map(toCatalogTag),
      truncated: items.length >= ORG_TAG_PAGE_CAP,
      loading: query.isLoading,
    };
  }, [query.data, query.isLoading]);
}

/**
 * A math.js-safe alias for a tag, unique within `existing`.
 *
 * Derived from the tag's NAME, never its id. Fixture ids happened to be
 * human-readable (`tag-furn1-bath-temp`), but live ids are v7 UUIDs — deriving
 * from those yields aliases like `4f2a9b1c` (or a leading-digit identifier that
 * math.js rejects outright), which fails server-side expression validation. #165.
 *
 * Tries the last segment first ("bath_temp"), widening until unique, mirroring
 * the behaviour the fixture-era `makeAliasFromTagId` had.
 */
export function makeAliasFromTag(
  tag: Pick<CatalogTag, "id" | "displayName">,
  existing: ComputedTagInput[],
): string {
  const basis = (tag.displayName || tag.id)
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
  const parts = basis.split("_").filter(Boolean);
  const inUse = new Set(existing.map((e) => e.alias));

  const safe = (s: string) => (/^[0-9]/.test(s) ? `t_${s}` : s);

  for (let n = 1; n <= parts.length; n++) {
    const cand = safe(parts.slice(-n).join("_"));
    if (cand && !inUse.has(cand)) return cand;
  }
  // Everything collided (or the name had no usable characters at all).
  let i = existing.length;
  let cand = safe(`${basis || "input"}_${i}`);
  while (inUse.has(cand)) cand = safe(`${basis || "input"}_${++i}`);
  return cand;
}
